import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type AttendanceVerificationMethodConfig,
  type ShiftStateMetadata,
} from "@src/appV2/Shifts/Shift/ShiftState/types";
import { useIsTouchpointIntegrationEnabled } from "@src/appV2/Shifts/Shift/TouchpointIntegration/useIsTouchpointIntegrationEnabled";
import { type Shift } from "@src/appV2/Shifts/Shift/types";

import { useIsTimeclockComplianceVideoProofEnabled } from "../TimeclockComplianceVideoProof/utils/useIsTimeclockComplianceVideoProofEnabled";
import { useIsTimeclockComplianceVideoValidationEnabled } from "../TimeclockComplianceVideoProof/utils/useIsTimeclockComplianceVideoValidationEnabled";
import { useIsTimekeepingIntegrationsEnabled } from "../TimekeepingIntegrations/useIsTimekeepingIntegrationsEnabled";

interface UseLegacyAttendanceVerificationMethodParams {
  shift?: Pick<Shift, "nfcTag" | "nfcTagRequests" | "facility" | "geofence">;
  shiftStateMetadata?: ShiftStateMetadata;
}

/**
 * Returns all enabled attendance verification methods from legacy configuration.
 * Methods are returned in priority order (highest priority first).
 */
export function useLegacyAttendanceVerificationMethods(
  params: UseLegacyAttendanceVerificationMethodParams
): AttendanceVerificationMethodConfig[] {
  const { shift, shiftStateMetadata } = params;

  const isNfcEnabled = shift?.nfcTag === true;

  const touchpointIntegrationConfig = useIsTouchpointIntegrationEnabled({
    integrationConfig: shiftStateMetadata?.touchpointIntegration,
  });
  const isTouchpointEnabled = isDefined(touchpointIntegrationConfig);

  const timekeepingIntegrationsConfig = useIsTimekeepingIntegrationsEnabled({
    config: shiftStateMetadata?.externalTimekeepingIntegrationsConfig,
  });
  const isTimekeepingIntegrationsEnabled = isDefined(timekeepingIntegrationsConfig);

  const { hcfTimeclockComplianceRollout, hcfTimeclockComplianceVideoValidationRollout } =
    shiftStateMetadata ?? {};

  const { isTimeclockComplianceVideoProofEnabled } = useIsTimeclockComplianceVideoProofEnabled(
    hcfTimeclockComplianceRollout
  );

  const isTimeclockComplianceVideoValidationEnabled =
    useIsTimeclockComplianceVideoValidationEnabled(hcfTimeclockComplianceVideoValidationRollout);

  const enabledMethods: AttendanceVerificationMethodConfig[] = [];

  if (isTimekeepingIntegrationsEnabled) {
    enabledMethods.push({
      type: AttendanceVerificationMethod.UKG_INTEGRATION,
      isEnabled: true,
      timeclockLocationString: timekeepingIntegrationsConfig.timeclockLocationString,
      pinsToEnterString: timekeepingIntegrationsConfig.pinsToEnterString,
      tutorial: timekeepingIntegrationsConfig.tutorial,
    });
  }

  if (isTouchpointEnabled) {
    enabledMethods.push({
      type: AttendanceVerificationMethod.MESH_INTEGRATION,
      isEnabled: true,
      kioskLocation: touchpointIntegrationConfig.kioskLocation,
      timeToAllowSkipInSec: touchpointIntegrationConfig.timeToAllowSkipInSec,
      asAgencyId: touchpointIntegrationConfig.agencyId,
      asAgencyName: touchpointIntegrationConfig.agencyName,
      timeclockLocationString: "",
      pinsToEnterString: "",
      tutorial: { stepsClockIn: [], stepsClockOut: [] },
    });
  }

  if (
    isTimeclockComplianceVideoValidationEnabled &&
    isDefined(hcfTimeclockComplianceVideoValidationRollout)
  ) {
    enabledMethods.push({
      isEnabled: true,
      type: AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
      timeclockLocationString: hcfTimeclockComplianceVideoValidationRollout.timeclockLocation ?? "",
      tutorial: { stepsClockIn: [], stepsClockOut: [] },
      pinsToEnterString: "",
    });
  }

  if (isTimeclockComplianceVideoProofEnabled) {
    enabledMethods.push({
      isEnabled: true,
      type: AttendanceVerificationMethod.TAKE_A_VIDEO,
      timeclockLocationString: hcfTimeclockComplianceRollout?.timeclockLocation ?? "",
      tutorial: { stepsClockIn: [], stepsClockOut: [] },
      pinsToEnterString: "",
    });
  }

  if (isNfcEnabled) {
    enabledMethods.push({
      type: AttendanceVerificationMethod.NFC_TAG,
      isEnabled: true,
      tags:
        shift?.nfcTagRequests?.map((tag) => ({
          id: `${tag.tagLocation}-${tag.tagStatus}`,
          location: tag.tagLocation,
          status: tag.tagStatus,
        })) ?? [],
    });
  }

  enabledMethods.push({
    type: AttendanceVerificationMethod.GEOFENCE,
    isEnabled: true,
  });

  return enabledMethods;
}
